import { asStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { OfficePresetManifest } from "@bezoned/shared/schemas/office/office-preset-manifest.schema.js";
import type { TeamAvailableChannelsGetResponse } from "@bezoned/shared/types/api/team/team-available-channels-get.types.js";
import {
  Dropdown,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@fluentui/react-components";
import { Info20Regular } from "@fluentui/react-icons";

import {
  CHANNEL_SPECIAL_OPTIONS,
  getChannelSelectedOptions,
  getChannelValueLabel,
} from "../channel-assignment.helpers.js";
import type { ChannelDraftAssignment } from "../channel-assignment.helpers.js";

type NewPresetTable = OfficePresetManifest["tables"][number];

type AvailableChannel = TeamAvailableChannelsGetResponse["channels"][number];

const filterChannelsForStep2 = (
  channels: AvailableChannel[],
  currentMsChannelId: string | undefined,
  pairedChannelIds: Set<string>,
  initiallyAssignedChannelIds: Set<string>
): AvailableChannel[] =>
  channels.filter((channel) => {
    if (channel.msChannelId === currentMsChannelId) {
      return true;
    }

    if (pairedChannelIds.has(channel.msChannelId)) {
      return false;
    }

    return (
      !channel.used || initiallyAssignedChannelIds.has(channel.msChannelId)
    );
  });

interface ChannelStepContentProps {
  isChannelsLoading: boolean;
  unpairedNewTables: NewPresetTable[];
  channelAssignments: Record<string, ChannelDraftAssignment>;
  availableChannels: AvailableChannel[];
  allPairedChannelIds: Set<string>;
  initiallyAssignedChannelIds: Set<string>;
  channelNameById: Map<string, string>;
  onChannelAssignment: (tableId: string, value: string) => void;
}

export const ChannelStepContent = ({
  isChannelsLoading,
  unpairedNewTables,
  channelAssignments,
  availableChannels,
  allPairedChannelIds,
  initiallyAssignedChannelIds,
  channelNameById,
  onChannelAssignment,
}: ChannelStepContentProps) => {
  if (isChannelsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Loading channels..." />
      </div>
    );
  }

  return (
    <Table
      aria-label="Configure channels for unpaired tables"
      className="w-full table-fixed"
    >
      <TableHeader>
        <TableRow>
          <TableHeaderCell className="w-[45%]">Table</TableHeaderCell>
          <TableHeaderCell className="w-[55%]">
            <div className="flex items-center gap-1">
              <span>Channel</span>
              <Tooltip
                content="Each office table is connected with an MS Teams Channel"
                relationship="label"
                withArrow
              >
                <Info20Regular aria-label="Channel mapping info" />
              </Tooltip>
            </div>
          </TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {unpairedNewTables.map((table) => {
          const assignment = channelAssignments[table.id] ?? {
            mode: "create_new",
          };
          const currentChannelId =
            assignment.mode === "existing_channel"
              ? assignment.msChannelId
              : undefined;
          const filteredChannels = filterChannelsForStep2(
            availableChannels,
            currentChannelId,
            allPairedChannelIds,
            initiallyAssignedChannelIds
          );

          return (
            <TableRow key={table.id}>
              <TableCell className="w-[45%] overflow-hidden text-ellipsis whitespace-nowrap">
                {table.defaultName}
              </TableCell>
              <TableCell className="w-[55%]">
                <Dropdown
                  button={{ className: "w-full !min-w-0" }}
                  className="w-full !min-w-0"
                  onOptionSelect={asStableCallback((_event, data) => {
                    if (data.optionValue) {
                      onChannelAssignment(table.id, data.optionValue);
                    }
                  })}
                  selectedOptions={getChannelSelectedOptions(assignment)}
                  value={getChannelValueLabel(assignment, channelNameById)}
                >
                  <Option value={CHANNEL_SPECIAL_OPTIONS.notUsed}>
                    Do not use
                  </Option>
                  <Option value={CHANNEL_SPECIAL_OPTIONS.createNew}>
                    Create new
                  </Option>
                  {filteredChannels.map((channel) => (
                    <Option
                      key={channel.msChannelId}
                      value={channel.msChannelId}
                    >
                      {channel.name}
                    </Option>
                  ))}
                </Dropdown>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
